import { type AffiliatePartnerData } from "~/domains/affiliate/types/models";
import { useTranslation } from "react-i18next";
import { useIsMinPhoneSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Box, Button, styled, Typography } from "@schaeffler-commerce/design-system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface Props {
  className?: string;
  sx?: import("@schaeffler-commerce/design-system").SxProps;
  affiliate: AffiliatePartnerData;
  onSaveClick: () => void;
  onUndoClick: () => void;
  isSaved: boolean;
}

export interface AffiliateSavePartnerTileViewProps {
  className?: string;
  sx?: import("@schaeffler-commerce/design-system").SxProps;
  logo?: string;
  isSaved: boolean;
  isPhone: boolean;
  isTablet: boolean;
  saveText: string;
  savedText: string;
  saveButtonLabel: string;
  undoButtonLabel: string;
  onSaveClick: () => void;
  onUndoClick: () => void;
}

const SavePartnerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile: boolean }>(({ isMobile, theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(2.5),
  ...(isMobile && {
    flexDirection: "row",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    margin: 0,
    padding: 0,
    gap: 0,
  }),
}));

const SavePartnerIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.textLabel.main,
  fontSize: 24,
}));

const UndoIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.primary.main,
  fontSize: 24,
}));

const PartnerLogo = styled("img")(() => ({
  width: 60,
  height: "auto",
}));

const PartnerSeparator = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  height: 60,
  margin: `0 ${theme.spacing(1.5)}`,
}));

export const AffiliateSavePartnerTileView = (props: AffiliateSavePartnerTileViewProps) => {
  const {
    className,
    sx,
    logo,
    isSaved,
    isPhone,
    isTablet,
    saveText,
    savedText,
    saveButtonLabel,
    undoButtonLabel,
    onSaveClick,
    onUndoClick,
  } = props;
  let btnProps;
  if (!isSaved) {
    btnProps = {
      onClick: onSaveClick,
      children: saveButtonLabel,
      "data-test-id": "banner-partner-save",
    };
  } else {
    btnProps = {
      onClick: onUndoClick,
      children: undoButtonLabel,
      "data-test-id": "banner-partner-undo",
    };
  }

  return (
    <SavePartnerContainer
      className={className}
      isMobile={isPhone}
      sx={sx}
    >
      {logo && isPhone && (
        <>
          <PartnerLogo src={logo} />
          <PartnerSeparator />
        </>
      )}

      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
        {isSaved ? <UndoIcon /> : <SavePartnerIcon />}
        <Typography sx={{ m: 0, ...(isTablet && { typography: "titleMedium" }) }}>
          {!isSaved ? saveText : savedText}
        </Typography>
      </Box>

      <Button {...btnProps} />
    </SavePartnerContainer>
  );
};

export const AffiliateSavePartnerTile = ({ affiliate, isSaved, onSaveClick, onUndoClick, className, sx }: Props) => {
  const { t } = useTranslation();
  const isPhone = useIsMinPhoneSize();
  const isTablet = useIsMinTabletSize();

  return (
    <AffiliateSavePartnerTileView
      className={className}
      sx={sx}
      logo={affiliate.logo}
      isSaved={isSaved}
      isPhone={isPhone}
      isTablet={isTablet}
      saveText={t("affiliateSalesPartner.banner.text.save")}
      savedText={t("affiliateSalesPartner.banner.text.saved")}
      saveButtonLabel={t("affiliateSalesPartner.banner.button.save")}
      undoButtonLabel={t("common.undo")}
      onSaveClick={onSaveClick}
      onUndoClick={onUndoClick}
    />
  );
};
